/**
 * The media library as tiles - the default view, and the one that answers "do I
 * recognise this picture" faster than any filename can.
 *
 * The tile used to carry two floating circles that only existed on hover, one
 * to download and one to delete. They are the row menu now, same as the list:
 * one button, in the tab order, with the same three verbs behind it. A tile and
 * a row are two pictures of one file and should not offer different things.
 */
import type { MediaAsset } from "../../../lib/media";
import { gridZoomStyle } from "../../../components/GridZoomControl";
import { MediaRowMenu } from "./MediaRowMenu";
import { MediaThumb } from "./MediaThumb";
import { formatBytes } from "./lib";

export function MediaGrid({
  items,
  tile,
  onDelete,
}: {
  items: MediaAsset[];
  /** Minimum tile width in pixels, straight from the zoom control. */
  tile: number;
  onDelete: (m: MediaAsset) => void;
}) {
  return (
    <div className="grid gap-3 p-3.5" style={gridZoomStyle(tile)}>
      {items.map((m) => (
        <div
          key={m.id}
          className="group relative aspect-square overflow-hidden rounded-2xl bg-card shadow-soft transition-all hover:shadow-elevated"
        >
          {/* The whole tile opens the file; the menu sits on top of the overlay. */}
          <a
            href={m.url}
            target="_blank"
            rel="noreferrer noopener"
            title={m.originalFilename}
            aria-label={m.originalFilename}
            className="absolute inset-0 z-[1]"
          />
          <MediaThumb asset={m} />

          <MediaRowMenu
            asset={m}
            onDelete={() => onDelete(m)}
            className="absolute right-1.5 top-1.5 z-[2] opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 has-[.crm-pop.is-open]:opacity-100"
          />

          {/* The filename strip. Always readable on hover rather than always on:
              the point of this view is the picture, and a caption across every
              tile is a caption across the picture. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
            <div className="truncate text-[11px] font-semibold text-white">{m.originalFilename}</div>
            <div className="crm-tnum text-[10px] text-white/70">{formatBytes(m.sizeBytes)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
